import { TREE_TYPE } from 'editor-utils/template';
import { templateGeneratorRequest, templateTocRequest } from '../lib/templateRequest';

const TREE_TYPE_DIRECTION = {
  [TREE_TYPE.SINGLE]: 'ancestors',
  [TREE_TYPE.COMBO]: 'combination',
  [TREE_TYPE.DESC]: 'descendants',
};

export const getDirectionFromTreeType = (treeType, sku) => {
  return TREE_TYPE_DIRECTION[
    treeType ?? (sku.indexOf('-desc') > 0 ? TREE_TYPE.DESC : TREE_TYPE.SINGLE)
  ];
};

/**
 * Generator API
 * @param {string} accessToken
 * @param {object} template
 * @param {boolean} async
 * @returns {object}
 */
const generatorApi = (accessToken, template, async = false) => ({
  /**
   * Photos data.
   * @param {Array<object>} photos
   * @param {string} initialPageId
   * @returns {object}
   */
  async getPhotoData(photos = [], initialPageId = '') {
    try {
      const pageId = initialPageId && initialPageId.length > 0 ? initialPageId : undefined;
      const pagesPhotos = photos.map(photo => ({
        key: photo.key,
        url: photo.url,
        width: photo.width,
        height: photo.height,
      }));

      let data;
      if (template.cover) {
        const coverPhoto = pagesPhotos.shift();
        data = {
          photos: pagesPhotos,
          cover: {
            cover_id: pageId,
            photos: [coverPhoto],
          },
        };
      } else {
        data = {
          photos: pagesPhotos,
          page_id: pageId,
        };
      }

      const response = await templateGeneratorRequest(
        accessToken,
        template,
        async,
        data,
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      console.warn(template.product_id, template.version, e);
      return { error: e.body || e.code };
    }
  },

  /**
   * Events data.
   * @param {Array<object>} events
   * @param {string} initialMonth
   * @param {string} initialDay
   * @returns {object}
   */
  async getEventsData(events = [], initialMonth, initialDay) {
    try {
      const response = await templateGeneratorRequest(accessToken, template, async, {
        start_month: initialMonth,
        start_weekday: initialDay,
        events: events.map(event => ({ ...event })), // TODO
      });

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      console.warn(template.product_id, template.version, e);
      return { error: e.body || e.code };
    }
  },

  /**
   * Ancestry.
   * @param {string} type
   * @param {object} ancestryAuth
   * @param {Array<string>} optionIds
   * @param {Array<object>} data
   * @returns {{ data: Array, ancestryToken: string, error: object }}
   */
  async getAncestryData(type = "ancestry", ancestryAuth, optionIds = [], data = []) {
    const { data: ancestryToken, error: ancestryTokenError } = await ancestryAuth.getToken(type);
    if (ancestryTokenError) {
      return { error: ancestryTokenError };
    }

    try {
      const response = await templateGeneratorRequest(
        accessToken,
        template,
        async,
        data.map(item => ({
          [type]: {
            ...item,
            accessToken: ancestryToken,
          },
          optionIds,
        })),
      );

      const json = await response.json();

      return { data: json.data, ancestryToken };
    } catch (error) {
      return { ancestryToken, error };
    }
  },

  /**
   * MyCanvas.
   * @param {Array<string>} optionIds
   * @param {Array<object>} data
   * @returns {{ data: Array, error: object }}
   */
  async getMyCanvasData(optionIds = [], data = []) {
    try {
      const response = await templateGeneratorRequest(
        accessToken,
        template,
        async,
        data.map(item => ({
          mycanvas: {
            ...item,
          },
          optionIds,
        })),
      );

      const json = await response.json();

      return { data: json.data };
    } catch (error) {
      return { error };
    }
  },

  /**
   * Table of Contents.
   * @param {object} data
   * @param {object} signal
   * @returns {object}
   */
  async getTocData(data, signal) {
    try {
      const response = await templateTocRequest(accessToken, template, data, signal);

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.body || e.code };
    }
  },
});

export default generatorApi;
